/**
 * <LinkDisplay> — web full-screen URL viewer (molecule, L0.1).
 *
 * YouTube → embedded `react-player`; otherwise a CTA that opens the URL via `Linking`
 * (parity with legacy `MaterialDisplayModal` on web — no WebView). Close chrome from
 * `<Overlay withCloseButton>`.
 */
import React, { useEffect, useState } from "react"
import { Linking, View } from "react-native"
import ReactPlayer from "react-player/youtube"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Overlay } from "../../atoms/Overlay"
import { isYoutubeUrl, sanitizeYoutubeUrl } from "../../utils/linkDisplayUtils"

export type LinkDisplayProps = {
    visible: boolean
    url: string
    onClose: () => void
    testID?: string
}

export function LinkDisplay(props: LinkDisplayProps) {
    const { visible, url, onClose, testID } = props
    const [youtubeUrl, setYoutubeUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!visible) return
        setYoutubeUrl(isYoutubeUrl(url) ? sanitizeYoutubeUrl(url) : null)
    }, [visible, url])

    return (
        <Overlay visible={visible} onRequestClose={onClose} withCloseButton placement="center" testID={testID}>
            <View style={styles.container}>
                <View style={styles.body}>
                    {youtubeUrl != null ? (
                        <ReactPlayer url={youtubeUrl} playing controls width="100%" height="100%" />
                    ) : (
                        <Button
                            label="Ouvrir le lien"
                            iconName="link"
                            onPress={() => {
                                if (url) Linking.openURL(url).catch(() => {})
                            }}
                        />
                    )}
                </View>
            </View>
        </Overlay>
    )
}

const styles = StyleSheet.create((theme) => ({
    container: {
        width: "95%" as const,
        height: "95%" as const,
        backgroundColor: theme.colors.surface,
    },
    body: {
        flex: 1,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        paddingTop: theme.spacing["12"],
        paddingHorizontal: theme.spacing["4"],
    },
}))
